import { useCallback, useEffect, useState } from 'react';
import {
    SIDEBAR_GROUPS,
    SIDEBAR_GROUPS_STORAGE_KEY,
    findGroupForPath,
    getDefaultGroupExpansion,
} from '../config/sidebarGroups';

export function useSidebarGroupState(pathname: string) {
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
        const defaults = getDefaultGroupExpansion();
        try {
            const stored = localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as Record<string, boolean>;
                const validIds = new Set(Object.keys(defaults));
                const sanitized = Object.fromEntries(
                    Object.entries(parsed).filter(([id]) => validIds.has(id)),
                );
                return { ...defaults, ...sanitized };
            }
        } catch {
            /* ignore */
        }
        return defaults;
    });

    // Auto-expand the group containing the active route
    useEffect(() => {
        const groupId = findGroupForPath(pathname);
        if (!groupId) return;
        setExpandedGroups(prev => {
            if (prev[groupId]) return prev;
            const next = { ...prev, [groupId]: true };
            localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    }, [pathname]);

    const toggleGroup = useCallback((groupId: string) => {
        setExpandedGroups(prev => {
            const next = { ...prev, [groupId]: !prev[groupId] };
            localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    }, []);

    const groupSummaries = SIDEBAR_GROUPS.map(g => ({
        id: g.id,
        label: g.label,
        count: g.items.length,
        isExpanded: expandedGroups[g.id] ?? g.defaultExpanded,
    }));

    return { expandedGroups, toggleGroup, groupSummaries };
}
